import { Flex, Text } from '@chakra-ui/react';

import { accent, font, fontSize, neutral, primaryAction } from 'styles/revampTokens';

import FigmaPropertyTabIcon from '../icons/FigmaPropertyTabIcon';
import FigmaDataTabIcon from '../icons/FigmaDataTabIcon';
import FigmaMapTabIcon from '../icons/FigmaMapTabIcon';
import FigmaAddIndicatorPlusIcon from '../icons/FigmaAddIndicatorPlusIcon';

export type BuilderModeTab = 'Property' | 'Data' | 'Map';

// 14px — PlanRevamp's PLAN_TOOLBAR_ICON_SIZE, so these tabs' icons match
// PlanViewModeTabs' size instead of the larger raw Figma-artboard values.
const TAB_ICON_SIZE = 14;

const TABS: { key: BuilderModeTab; label: string; icon: (color: string) => JSX.Element }[] = [
  {
    key: 'Property',
    label: 'Property',
    icon: (color) => (
      <FigmaPropertyTabIcon width={TAB_ICON_SIZE} height={TAB_ICON_SIZE} color={color} />
    ),
  },
  {
    key: 'Data',
    label: 'Data',
    icon: (color) => (
      <FigmaDataTabIcon width={TAB_ICON_SIZE} height={TAB_ICON_SIZE} color={color} />
    ),
  },
  {
    key: 'Map',
    label: 'Map',
    icon: (color) => <FigmaMapTabIcon width={TAB_ICON_SIZE} height={TAB_ICON_SIZE} color={color} />,
  },
];

interface Properties {
  activeTab: BuilderModeTab;
  onTabChange: (tab: BuilderModeTab) => void;
  onAddIndicator: () => void;
}

// Property/Data/Map tab row + "Add New Indicator" button. Padding (12px, not
// the Figma artboard's 72px) matches `BuilderModeHeader`/PlanRevamp's
// `PlanPageTitleBar`. Active-tab treatment (accent.subtle fill + 2px
// accent.solid bottom border) and the 44px row height follow PlanRevamp's
// `PlanViewModeTabs`/`PLAN_TOOLBAR_HEIGHT` exactly, so switching tabs reads
// the same way across both modules.
//
// The Add button sits inside this same 44px row (not in its own taller
// wrapper) so its center lines up with the tabs instead of floating higher.
const CategoryTabsBar = ({ activeTab, onTabChange, onAddIndicator }: Properties) => {
  return (
    <Flex
      as='nav'
      bg={neutral.surface}
      borderBottom='1px solid'
      borderColor={neutral.border}
      alignItems='center'
      justifyContent='space-between'
      px='12px'
    >
      <Flex alignItems='stretch' h='44px'>
        {TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <Flex
              key={tab.key}
              as='button'
              type='button'
              alignItems='center'
              gap='6px'
              px='12px'
              bg={isActive ? accent.subtle : 'transparent'}
              borderBottom='2px solid'
              borderColor={isActive ? accent.solid : 'transparent'}
              cursor='pointer'
              transition='background 0.12s ease, border-color 0.12s ease'
              _hover={{ bg: isActive ? accent.subtle : neutral.surfaceHover }}
              onClick={() => onTabChange(tab.key)}
            >
              {tab.icon(primaryAction.bg)}
              <Text
                fontFamily={font.body}
                fontSize={fontSize.body}
                fontWeight={500}
                color={primaryAction.bg}
              >
                {tab.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      <Flex
        as='button'
        type='button'
        alignItems='center'
        gap='6px'
        px='12px'
        h='44px'
        cursor='pointer'
        transition='background 0.12s ease'
        _hover={{ bg: neutral.surfaceHover }}
        onClick={onAddIndicator}
      >
        <FigmaAddIndicatorPlusIcon width={12} height={12} color={accent.solid} />
        <Text
          fontFamily={font.body}
          fontSize={fontSize.body}
          fontWeight={500}
          color={primaryAction.bg}
          whiteSpace='nowrap'
        >
          Add New Indicator
        </Text>
      </Flex>
    </Flex>
  );
};

export default CategoryTabsBar;
